import React, { useState } from 'react';
import { TopicCluster, Recommendation } from '../../data/types';
import { TrendBadge } from './TrendBadge';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Lightbulb, CheckCircle2, ArrowDownRight, ArrowUpRight } from 'lucide-react';
import clsx from 'clsx';
interface HotTopicItemProps {
  topic: TopicCluster;
  index: number;
  kpiBucketName: string;
  recommendation?: Recommendation;
}
export function HotTopicItem({
  topic,
  index,
  kpiBucketName,
  recommendation
}: HotTopicItemProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const isConcern = topic.type === 'concern';
  return <motion.div initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay: index * 0.05
  }} className="mb-3 overflow-hidden rounded-lg border border-white/5 bg-white/5 transition-colors hover:bg-white/10" data-id="element-85">
      <div onClick={() => setIsExpanded(!isExpanded)} className="flex cursor-pointer items-center justify-between p-4" data-id="element-86">
        <div className="flex items-center space-x-4" data-id="element-87">
          <div className={clsx('w-1.5 h-10 rounded-full', isConcern ? 'bg-danger/50' : 'bg-success/50')} data-id="element-88" />
          <div data-id="element-89">
            <div className="flex items-center space-x-2" data-id="element-90">
              <h4 className="text-sm font-medium text-white" data-id="element-91">{topic.name}</h4>
              {recommendation && <span className="flex items-center rounded bg-accent/10 px-1.5 py-0.5 text-[10px] font-medium text-accent" data-id="element-92">
                  <Lightbulb className="mr-1 h-3 w-3" data-id="element-93" />
                  AI Insight
                </span>}
            </div>
            <div className="mt-1 flex items-center space-x-3" data-id="element-94">
              <span className="text-xs text-secondary" data-id="element-95">
                {topic.mentionCount} mentions
              </span>
              <span className="text-xs text-secondary" data-id="element-96">•</span>
              {/* KPI Influence Badge */}
              <div className={clsx('flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium', isConcern ? 'bg-danger/10 text-danger' : 'bg-success/10 text-success')} data-id="element-97">
                {isConcern ? <ArrowDownRight className="mr-1 h-3 w-3" data-id="element-98" /> : <ArrowUpRight className="mr-1 h-3 w-3" data-id="element-99" />}
                {kpiBucketName}
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-6" data-id="element-100">
          {/* Sentiment Bar */}
          <div className="hidden w-24 flex-col items-end sm:flex" data-id="element-101">
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/10" data-id="element-102">
              <div className={clsx('h-full rounded-full', isConcern ? 'bg-danger' : 'bg-success')} style={{
              width: `${topic.sentimentRatio * 100}%`
            }} data-id="element-103" />
            </div>
            <span className="mt-1 text-[10px] text-secondary" data-id="element-104">
              {Math.round(topic.sentimentRatio * 100)}% Positive
            </span>
          </div>

          <TrendBadge trend={topic.trend === 'up' ? 10 : topic.trend === 'down' ? -10 : 0} inverse={isConcern} data-id="element-105" />

          <motion.div animate={{
          rotate: isExpanded ? 180 : 0
        }} transition={{
          duration: 0.2
        }} data-id="element-106">
            <ChevronDown className="h-5 w-5 text-secondary" data-id="element-107" />
          </motion.div>
        </div>
      </div>

      <AnimatePresence data-id="element-108">
        {isExpanded && recommendation && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} data-id="element-109">
            <div className="border-t border-white/5 bg-black/20 p-4 pl-10" data-id="element-110">
              <div className="border-l-2 border-accent pl-4" data-id="element-111">
                <div className="mb-2 flex items-center justify-between" data-id="element-112">
                  <h5 className="text-sm font-semibold text-white" data-id="element-113">
                    {recommendation.title}
                  </h5>
                  <span className="rounded bg-white/10 px-2 py-0.5 text-[10px] font-bold text-white" data-id="element-114">
                    {recommendation.expectedImpact} Impact
                  </span>
                </div>
                <p className="mb-3 text-xs leading-relaxed text-secondary" data-id="element-115">
                  {recommendation.whyItMatters}
                </p>
                <div className="space-y-2" data-id="element-116">
                  {recommendation.actionItems.map((item, idx) => <div key={idx} className="flex items-start text-xs text-gray-300" data-id="element-117">
                      <CheckCircle2 className="mr-2 mt-0.5 h-3.5 w-3.5 shrink-0 text-success" data-id="element-118" />
                      <span data-id="element-119">{item}</span>
                    </div>)}
                </div>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}